import React from 'react';
import propTypes from 'prop-types';

import ConfirmationModal from 'app/ConfirmationModal/components/ConfirmationModal';
import FolderAndPageList from 'app/FolderAndPageList/components/FolderAndPageList';
import { CONTENT_TYPES, REAL_ROOT_FOLDER_ID, SITE_ROOT_FOLDER_ID_NORDSTROM } from 'lib/const';
import { noop } from 'lib/noop';
import { IconButton } from 'ui/Components';
import { folder, parentDirectory } from 'ui/Icons';

import styles from './SelectFolderOrPage.css';

/**
 * confirmation modal for moving content to another folder
 *
 * @param {object} props - component props
 * @param {object} props.folderTree - object of folder data keyed by folder id
 * @param {Function} props.getFolder - make request to get more folder data
 * @param {string} props.initialFolderId - initial folder id to display
 * @param {boolean} props.isOpen - modal open/close state
 * @param {Function} props.onCancel - function to call on cancel
 * @param {Function} props.onEnter - function to call on enter (positive confirmation)
 * @returns {React.Component} move content modal
 */
const SelectFolderOrPage = ({
  folderTree,
  getFolder,
  initialFolderId,
  isOpen,
  onCancel,
  onEnter
}) => {
  // internal `folderId` state is tied to external `initialFolderId` prop
  const [folderId, setFolderId] = React.useState(initialFolderId);
  React.useEffect(() => setFolderId(initialFolderId), [initialFolderId]);

  // ensure data exists
  if (folderId && !folderTree[folderId]) {
    getFolder(folderId);
  }

  const { [folderId]: details } = folderTree;
  const folders = details?.Children?.filter(({ Type }) => Type === CONTENT_TYPES.FOLDER);
  const name = details?.Name;
  const parentFolderId = details?.Breadcrumb?.slice(-1).pop()?.Id ?? REAL_ROOT_FOLDER_ID;

  // prevent moving into real root
  const renderParentFolderButton = parentFolderId !== REAL_ROOT_FOLDER_ID;

  return (
    <ConfirmationModal
      headerText="Select a destination folder for the move:"
      isOpen={isOpen}
      onNoClick={onCancel}
      noButtonLabel="Cancel"
      onYesClick={() => onEnter({ id: folderId, name })}
      yesButtonLabel="Move Here"
    >
      <button
        hidden={!renderParentFolderButton}
        className={styles.parentDirectory}
        type="button"
        onClick={() => setFolderId(parentFolderId)}
      >
        <IconButton alt="Parent Directory" icon={parentDirectory} noMargin rem={2} />
        &nbsp;
        <span>(Parent Directory)</span>
      </button>
      <span className="d-flex align-items-center border-bottom border-light pb-1">
        <IconButton alt="Folder" icon={folder} rem={2.3} noMargin />
        &nbsp;
        <strong>{name}</strong>
      </span>
      <FolderAndPageList
        foldersAndPages={folders}
        onItemSelect={child => setFolderId(child.Id)}
      />
    </ConfirmationModal>
  );
};

SelectFolderOrPage.propTypes = {
  folderTree: propTypes.object,
  getFolder: propTypes.func,
  initialFolderId: propTypes.string,
  isOpen: propTypes.bool,
  onCancel: propTypes.func,
  onEnter: propTypes.func
};

SelectFolderOrPage.defaultProps = {
  folderTree: {},
  getFolder: noop,
  isOpen: false,
  initialFolderId: SITE_ROOT_FOLDER_ID_NORDSTROM,
  onCancel: noop,
  onEnter: noop
};

export default SelectFolderOrPage;
